/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import NoTransactionIcon from '../assets/svg/NoTransactionIcon';
import { Pending, Cancel } from '../assets/svg/StatusIcon';
import { RightArrow } from '../assets/svg/ArrowIcon';
import { Activity, webFormatCurrency } from '@rb-serve-app/core';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { EventType, useAnalytics } from '../ui/Analytics';
import { formatAmount, webformatDate } from '@rb-serve-app/core';
import { useSelector } from 'react-redux';
import { TutorialStep } from './InteractiveTutorial';

type Props = {
  transactionDetails: Activity[] | undefined;
  contentfulData: any;
  assets: any;
};

// interface GroupedByDate {
//   [date: string]: Activity[];
// }

// Function to group transactions by date
// function groupTransactionsByDate(transactions: Activity[]): GroupedByDate {
//   const groupedByDate: GroupedByDate = {};
//   if (Array.isArray(transactions)) {
//     transactions.forEach((transaction) => {
//       const date = transaction.date;
//       if (!groupedByDate[date]) {
//         groupedByDate[date] = [];
//       }
//       groupedByDate[date].push(transaction);
//     });
//   }
//   return groupedByDate;
// }

const RecentTransaction: React.FC<Props> = ({
  contentfulData,
  assets,
  transactionDetails = [],
}) => {
  const { trackEvent } = useAnalytics();
  const { transactions = {}, loading } = useSelector(
    (state: any) => state.transactions || {}
  );
  const lang = localStorage.getItem('language') || 'en-US';

  const setupLocale = () => {
    try {
      const localeToUse = lang?.includes('fr') ? 'fr' : 'en';
      moment.locale(localeToUse);
      return localeToUse;
    } catch (error) {
      console.warn('Error setting moment locale:', error);
      moment.locale('en');
      return 'en';
    }
  };

  setupLocale();
  const formatDate = (dateString: string) => moment(dateString).format('ll');

  const navigate = useNavigate();

  const gotoTransactionPage = () => {
    trackEvent(
      EventType.CLICK_EVENT,
      'dashboard',
      window.location.href,
      'dashboard|view all transactions'
    );
    navigate('/transactions');
  };

  return (
    <div
      className="bg-white lg:pt-5 rounded-lg lg:shadow-recent-transactions md:shadow-none sm:mt-0 sm:shadow-none flex flex-col h-full"
      role="region"
      aria-labelledby="payment-due-title"
    >
      <h2
        id="recent-transaction"
        className="text-[16px] font-[650] md:mb-0 md:p-0 mb-[20px]"
        aria-label={contentfulData?.recentTransactionsTitle}
      >
        {contentfulData?.recentTransactionsTitle}
      </h2>
      {loading ? (
        <></>
      ) : transactions && transactions?.length > 0 ? (
        <div className="overflow-auto md:p-0 sm:p-[2%] flex-grow min-h-0">
          <table className="w-full sm:min-w-[200px] md:min-w-[640px] table-auto">
            <thead className="hidden md:table-header-group md:border-b">
              <tr className="text-left hidden md:table-row">
                <th
                  className="py-2 text-ts-h-color font-[500] text-[16px]"
                  aria-label={contentfulData?.transactionDescriptionTitle}
                >
                  {contentfulData?.transactionDescriptionTitle}
                </th>
                <th className="px-4 py-1 text-white hidden md:table-cell">
                  {/* Status */}
                </th>
                <th
                  aria-label={contentfulData?.dateTitle}
                  className="px-4 py-1 text-ts-h-color font-[500] text-[16px] hidden md:table-cell"
                >
                  {contentfulData?.dateTitle}
                </th>
                <th
                  aria-label={contentfulData?.amountTitle}
                  className="pl-10 py-1 text-ts-h-color font-[500] text-[16px] text-right pr-10"
                >
                  {contentfulData?.amountTitle}
                </th>
              </tr>
            </thead>
            <tbody className="text-[16px]">
              {transactionDetails.map((transaction, index) => (
                <tr
                  key={index + transaction.merchant.name}
                  className="border-b hover:bg-gray-100"
                >
                  <td className="py-3 pl-2  flex-col md:flex md:flex-row">
                    {(index === 0 ||
                      transactionDetails[index - 1].date !==
                        transaction.date) && (
                      <span
                        className="block md:hidden text-gray-400"
                        aria-label={webformatDate(transaction.date, lang)}
                      >
                        {webformatDate(transaction.date, lang)}
                      </span>
                    )}
                    {transaction.merchant.name}
                    <div className="block md:hidden">
                      {transaction.activityStatus && (
                        <div className="text-gray-400 flex">
                          {transaction.activityStatus === 'PENDING' ? (
                            <div className="flex items-center">
                              <span className="material-icons">
                                <Pending />
                              </span>
                              <span
                                className="ml-1"
                                aria-label={contentfulData?.pendingStatusTitle}
                              >
                                {contentfulData?.pendingStatusTitle}
                              </span>
                            </div>
                          ) : transaction.activityStatus === 'DECLINED' ? (
                            <div className="flex items-center">
                              <span className="material-icons">
                                <Cancel />
                              </span>
                              <span
                                className="ml-1"
                                aria-label={contentfulData?.declinedStatusTitle}
                              >
                                {contentfulData?.declinedStatusTitle}
                              </span>
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="hidden md:table-cell">
                    {transaction.activityStatus && (
                      <div className="ml-2 text-gray-400 flex">
                        {transaction.activityStatus === 'PENDING' ? (
                          <div className="flex items-center">
                            <span className="material-icons">
                              <Pending />
                            </span>
                            <span
                              className="ml-1"
                              aria-label={contentfulData?.pendingStatusTitle}
                            >
                              {contentfulData?.pendingStatusTitle}
                            </span>
                          </div>
                        ) : transaction.activityStatus === 'DECLINED' ? (
                          <div className="flex items-center">
                            <span className="material-icons">
                              <Cancel />
                            </span>
                            <span
                              className="ml-1"
                              aria-label={contentfulData?.declinedStatusTitle}
                            >
                              {contentfulData?.declinedStatusTitle}
                            </span>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </td>
                  <td
                    className="px-4 py-1 hidden md:table-cell"
                    aria-label={webformatDate(transaction.date, lang)}
                  >
                    {webformatDate(transaction.date, lang)}
                  </td>
                  <td className="py-3 flex pl-2 pr-10 justify-end">
                    {transaction?.foreign ? (
                      <div className="flex flex-row relative pl-10">
                        <span className="material-icons absolute left-3 bottom-[1px]">
                          <img
                            src={assets?.fx_logo}
                            alt="foreignTransaction"
                            className="w-5 h-5"
                          />
                        </span>

                        <span aria-label={transaction.amount.value}>
                          {Number(transaction.amount.value) < 0
                            ? `-${webFormatCurrency(Math.abs(Number(transaction?.amount.value)), lang)}`
                            : `${webFormatCurrency(Number(transaction?.amount.value), lang)}`}
                        </span>
                      </div>
                    ) : (
                      <div className="flex flex-row pl-10">
                        <span
                          aria-label={transaction.amount.value}
                          className=""
                        >
                          {Number(transaction.amount.value) < 0
                            ? `-${webFormatCurrency(Math.abs(Number(transaction?.amount.value)), lang)}`
                            : `${webFormatCurrency(Number(transaction?.amount.value), lang)}`}
                        </span>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="flex flex-col flex-grow min-h-0">
          <div className="flex flex-col justify-center items-center my-auto">
            <NoTransactionIcon width="280" height="175" />
            <h3
              className="mt-1 text-gray-500"
              aria-label={contentfulData?.noTransactionsText}
            >
              {contentfulData?.noTransactionsText}
            </h3>
          </div>
        </div>
      )}
      <div className="text-left mt-auto pt-4">
        <TutorialStep id="view-all-transactions">
          <div
            className="flex group cursor-pointer"
            onClick={gotoTransactionPage}
          >
            <p
              className="text-primary-red font-[650] text-[16px]"
              aria-label={contentfulData?.viewAllTransactionsCtaAccText}
            >
              {contentfulData?.viewAllTransactionsCtaTitle}
            </p>
            <span className="hidden group-hover:block ml-2 w-200px transition-opacity duration-2000 ease-in-out">
              <RightArrow fill={'#DA291C'} />
            </span>
          </div>
        </TutorialStep>
      </div>
    </div>
  );
};
export default React.memo(RecentTransaction);
